import { IPerioChartData } from '@dataTypes/chartingFeature.types'
import { toDisplayOrder } from '@utils/perio.utils'

/** Three probe sites in left-to-right display order (mm). */
export type PerioSiteTriple = [number, number, number]

/** PD + GM for one surface on one chart. */
export interface PerioSurfaceSites {
  pd: PerioSiteTriple
  gm: PerioSiteTriple
}

export interface ToothPerioSites {
  facialA: PerioSurfaceSites
  lingualA: PerioSurfaceSites
  facialB?: PerioSurfaceSites
  lingualB?: PerioSurfaceSites
}

export type PerioSitesByTooth = Record<number, ToothPerioSites>

const EMPTY_TRIPLE: PerioSiteTriple = [0, 0, 0]

const parseMetricTriple = (
  toothId: number,
  raw: string | undefined,
): PerioSiteTriple => {
  const parts = (raw ?? '0 0 0').trim().split(/\s+/)
  while (parts.length < 3) {
    parts.push('0')
  }
  const storage = parts.slice(0, 3)
  const display = toDisplayOrder(toothId, storage)
  return [
    parseFloat(display[0]) || 0,
    parseFloat(display[1]) || 0,
    parseFloat(display[2]) || 0,
  ]
}

const readSurfaceSites = (
  chart: IPerioChartData | undefined,
  section: 'facial' | 'lingual',
  toothId: number,
): PerioSurfaceSites => {
  if (!chart) {
    return { pd: EMPTY_TRIPLE, gm: EMPTY_TRIPLE }
  }
  const sectionData = chart[section]
  return {
    pd: parseMetricTriple(toothId, sectionData?.PD?.[toothId - 1]),
    gm: parseMetricTriple(toothId, sectionData?.GM?.[toothId - 1]),
  }
}

/** Build per-tooth PD + GM sites (display order) for chart A and optional compare chart B. */
export const buildPerioSitesByTooth = (
  chartA: IPerioChartData | undefined,
  chartB?: IPerioChartData | undefined,
): PerioSitesByTooth => {
  const result: PerioSitesByTooth = {}
  for (let toothId = 1; toothId <= 32; toothId += 1) {
    const sites: ToothPerioSites = {
      facialA: readSurfaceSites(chartA, 'facial', toothId),
      lingualA: readSurfaceSites(chartA, 'lingual', toothId),
    }
    if (chartB) {
      sites.facialB = readSurfaceSites(chartB, 'facial', toothId)
      sites.lingualB = readSurfaceSites(chartB, 'lingual', toothId)
    }
    result[toothId] = sites
  }
  return result
}

/**
 * Pocket-floor mm from CEJ: gingival margin + pocket depth.
 * Positive GM is apical to CEJ (recession); negative is coronal.
 */
export const pocketFloorFromCej = (
  gm: PerioSiteTriple,
  pd: PerioSiteTriple,
): PerioSiteTriple => [gm[0] + pd[0], gm[1] + pd[1], gm[2] + pd[2]]

/**
 * Five drawing points: synthetic edge baselines (always 0) + three clinical sites.
 * Edges connect cleanly to neighboring teeth at CEJ.
 */
export type PerioSiteFive = [number, number, number, number, number]

export const toFivePointSeries = (clinical: PerioSiteTriple): PerioSiteFive => [
  0,
  clinical[0],
  clinical[1],
  clinical[2],
  0,
]

export type PerioLineSurface = 'facial' | 'lingual'

/**
 * Which surface to draw on a tooth part.
 * Full view: upper base = facial, lower base = lingual.
 * Upper/lower-only: buccal base = facial; palatal/lingual parts = lingual.
 */
export const getPerioSurfaceForPart = (
  part: 'base' | 'palatal' | 'lingual',
  toothId: number,
  view: 'full' | 'upper' | 'lower',
): PerioLineSurface => {
  if (part === 'palatal' || part === 'lingual') {
    return 'lingual'
  }
  if (view === 'full' && toothId >= 17 && toothId <= 32) {
    return 'lingual'
  }
  return 'facial'
}
